export type ConnectivityProbeOutcome =
  | 'reachable'
  | 'http-error'
  | 'invalid-json'
  | 'invalid-payload'
  | 'timeout'
  | 'superseded'
  | 'network-error';

export type ConnectivityHealthPhase = 'initial' | 'confirmation' | 'recovery';

type ProbeDetails = {
  probe: 'internet' | 'ath-status' | 'data-api-health';
  url: string;
  timeoutMs: number;
  phase?: ConnectivityHealthPhase;
};

export type ConnectivityHealthDecision = {
  phase: ConnectivityHealthPhase;
  timeoutMs: number;
  outage: 'suspected' | 'confirmed' | 'cleared';
  internetReachable: boolean | null;
};

const decisionObservers = new Set<(event: ConnectivityHealthDecision) => void>();

export function subscribeConnectivityHealthDecisions(
  observer: (event: ConnectivityHealthDecision) => void
): () => void {
  decisionObservers.add(observer);
  return () => decisionObservers.delete(observer);
}

export function reportConnectivityHealthDecision(event: ConnectivityHealthDecision): void {
  decisionObservers.forEach((observer) => {
    try {
      observer(event);
    } catch {
      // Diagnostics never affect connectivity decisions.
    }
  });
}

export type ConnectivityProbeEvent = ProbeDetails & {
  method: 'GET';
  startedAt: string;
  durationMs: number;
  outcome: ConnectivityProbeOutcome;
  httpStatus: number | null;
  errorMessage: string | null;
};

type ProbeObserver = (event: ConnectivityProbeEvent) => void;
const observers = new Set<ProbeObserver>();

/** No logger/store dependencies: observing a probe must never change its result. */
export function subscribeConnectivityProbes(observer: ProbeObserver): () => void {
  observers.add(observer);
  return () => observers.delete(observer);
}

/** One result per actual request, including body validation and deadline failures. */
export function beginConnectivityProbe(details: ProbeDetails) {
  const startedAt = Date.now();
  let completed = false;
  return (
    outcome: ConnectivityProbeOutcome,
    httpStatus: number | null = null,
    error?: unknown
  ): void => {
    if (completed) return;
    completed = true;
    observers.forEach((observer) => {
      try {
        observer({
          ...details,
          method: 'GET',
          startedAt: new Date(startedAt).toISOString(),
          durationMs: Math.max(0, Date.now() - startedAt),
          outcome,
          httpStatus,
          errorMessage: error instanceof Error ? error.message : null,
        });
      } catch {
        // Diagnostic failures never affect connectivity or other observers.
      }
    });
  };
}
